import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, Trash2, Upload, Download, Loader2, Link2 } from "lucide-react";
import { api, fmtINR, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["new", "contacted", "quoted", "closed"];

export default function Inquiries() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [items, setItems] = useState([{ description: "", amount: "" }]);
  const [notes, setNotes] = useState("");
  const [gst, setGst] = useState("0");
  const [sendMail, setSendMail] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/admin/inquiries").then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.patch(`/admin/inquiries/${id}`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  const openQuote = (q) => {
    setSel(q);
    const qi = q.quotation?.items;
    setItems(qi?.length ? qi.map((i) => ({ description: i.description, amount: String(i.amount) })) : [{ description: "", amount: "" }]);
    setNotes(q.quotation?.notes || "");
    setGst(String(q.quotation?.gst_percent ?? 0));
    setSendMail(true);
  };

  const subtotal = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);
  const gstAmt = Math.round(subtotal * (Number(gst) || 0)) / 100;
  const total = subtotal + gstAmt;

  const saveQuote = async () => {
    const clean = items.filter((i) => i.description && i.amount !== "").map((i) => ({ description: i.description, amount: Number(i.amount) }));
    if (!clean.length) return toast.error("Add at least one line item");
    setBusy(true);
    try {
      await api.post(`/admin/inquiries/${sel.id}/quotation`, { items: clean, notes, gst_percent: Number(gst) || 0, send_email: sendMail });
      toast.success("Quotation saved" + (sendMail ? " & emailed" : ""));
      setSel(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const uploadPdf = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/inquiries/${sel.id}/quotation/upload`, fd);
      toast.success("Quotation PDF uploaded");
      setSel(data); load();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/admin/inquiries/${q.id}/quotation/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${q.quotation?.quote_number || "quotation"}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("No quotation PDF"); }
  };

  const copyShareLink = (q) => {
    const link = fileUrl(q.quotation_file_path);
    navigator.clipboard?.writeText(link);
    toast.success("Share link copied");
  };

  return (
    <div>
      <h1 className="font-display text-3xl text-zinc-900 mb-6">Wedding & Custom Inquiries</h1>
      <div className="grid md:grid-cols-2 gap-4">
        {rows.map((q) => (
          <div key={q.id} className="bg-card border border-pink-200 rounded-xl p-5" data-testid={`inquiry-${q.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-zinc-900 font-medium">{q.name}</h3>
                <p className="text-sm text-zinc-600">{q.phone} · {q.email}</p>
              </div>
              <Select value={STATUSES.includes(q.status) ? q.status : "new"} onValueChange={(v) => setStatus(q.id, v)}>
                <SelectTrigger className="w-32 bg-white border-pink-200 text-zinc-900 h-8 text-xs" data-testid={`inq-status-${q.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="mt-3 text-sm text-zinc-700 space-y-1">
              <div><span className="text-zinc-500">Type:</span> {q.event_type}</div>
              {q.event_date && <div><span className="text-zinc-500">Date:</span> {q.event_date}</div>}
              {q.budget && <div><span className="text-zinc-500">Budget:</span> {q.budget}</div>}
              {q.message && <p className="text-zinc-600 mt-2 bg-white rounded-lg p-3">{q.message}</p>}
            </div>

            {(q.quotation || q.quotation_file_path) && (
              <div className="mt-3 text-xs bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 text-emerald-800">
                {q.quotation && <span>Quotation {q.quotation.quote_number} · {fmtINR(q.quotation.total)}</span>}
                {q.quotation_file_path && <span className="ml-2">PDF attached</span>}
              </div>
            )}

            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" onClick={() => openQuote(q)} data-testid={`quote-btn-${q.id}`} className="gold-gradient text-white font-semibold">
                <FileText size={14} className="mr-1" /> {q.quotation ? "Edit" : "Create"} Quotation
              </Button>
              {q.quotation && (
                <Button size="sm" variant="outline" onClick={() => downloadPdf(q)} data-testid={`quote-pdf-${q.id}`} className="border-pink-300 text-zinc-700">
                  <Download size={14} className="mr-1" /> PDF
                </Button>
              )}
              {q.quotation_file_path && (
                <Button size="sm" variant="outline" onClick={() => copyShareLink(q)} data-testid={`quote-share-${q.id}`} className="border-pink-300 text-zinc-700">
                  <Link2 size={14} className="mr-1" /> Share PDF
                </Button>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="col-span-2 py-16 text-center text-zinc-500">No inquiries yet</div>}
      </div>

      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="bg-card border-pink-200 text-zinc-900 max-w-lg max-h-[90vh] overflow-y-auto">
          {sel && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">Quotation · {sel.name}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-700">Line Items</Label>
                  {items.map((it, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <Input value={it.description} placeholder="Description" data-testid={`quote-item-desc-${idx}`}
                        onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))}
                        className="bg-white border-pink-200 flex-1" />
                      <Input type="number" value={it.amount} placeholder="₹" data-testid={`quote-item-amt-${idx}`}
                        onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, amount: e.target.value } : x))}
                        className="bg-white border-pink-200 w-28" />
                      <button onClick={() => setItems(items.length > 1 ? items.filter((_, i) => i !== idx) : items)} className="text-red-400 p-1"><Trash2 size={15} /></button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setItems([...items, { description: "", amount: "" }])} data-testid="quote-add-item" className="border-pink-300 text-zinc-700"><Plus size={14} className="mr-1" /> Add item</Button>
                </div>

                <div className="grid grid-cols-2 gap-3 items-end">
                  <div><Label className="text-zinc-700">GST %</Label><Input type="number" value={gst} data-testid="quote-gst" onChange={(e) => setGst(e.target.value)} className="mt-1 bg-white border-pink-200" /></div>
                  <div className="text-right text-sm">
                    <div className="text-zinc-500">Subtotal: {fmtINR(subtotal)}</div>
                    <div className="text-zinc-500">GST: {fmtINR(gstAmt)}</div>
                    <div className="text-zinc-900 font-semibold text-lg" data-testid="quote-total">Total: {fmtINR(total)}</div>
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-700">Notes (optional)</Label>
                  <Textarea value={notes} data-testid="quote-notes" onChange={(e) => setNotes(e.target.value)} className="mt-1 bg-white border-pink-200" rows={2} />
                </div>

                <label className="flex items-center gap-2 text-sm text-zinc-700">
                  <Checkbox checked={sendMail} onCheckedChange={setSendMail} data-testid="quote-sendmail" /> Email quotation to customer
                </label>

                <Button onClick={saveQuote} disabled={busy} data-testid="save-quote-btn" className="w-full gold-gradient text-white font-semibold rounded-full">
                  {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : null} Save Quotation
                </Button>

                <div className="border-t border-pink-100 pt-3">
                  <Label className="text-zinc-700 text-xs">Or upload a ready-made quotation PDF</Label>
                  <label className="mt-2 inline-flex items-center gap-2 text-sm text-gold cursor-pointer" data-testid="quote-upload-label">
                    <Upload size={15} /> Upload PDF
                    <input ref={fileRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => uploadPdf(e.target.files[0])} />
                  </label>
                  {sel.quotation_file_path && <span className="ml-2 text-xs text-emerald-700">✓ {sel.quotation_file_name || "file uploaded"}</span>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
